import { Head, Link, router, usePage } from "@inertiajs/react"
import { ChevronRight, Clock, Copy, MapPin, Play, Trash2 } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { AutomationsSubNav } from "@/components/automations/AutomationsSubNav"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import type { PageProps } from "@/types/inertia"
import type { AutomationCard, AutomationGroup } from "@/types/automation"
import type { LastRun } from "@/types/run"

const DESCRIPTION =
  "Turn automated text campaigns on for your organization, choose which Locals they reach, and set when they send."

export default function AutomationsIndex({
  running,
  available,
  can_edit,
}: {
  running: AutomationGroup[]
  available: AutomationGroup[]
  /** False with Read only: no Duplicate, no Remove, and "View" rather than "Set up". */
  can_edit: boolean
}) {
  const { props } = usePage<PageProps>()
  const viewing = props.viewing
  const orgName = viewing?.name ?? "your organization"

  return (
    <>
      <Head title="Automations">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Automations" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="Automations"
          description={
            viewing
              ? can_edit
                ? `What ${viewing.name} has turned on. These settings are shared — everyone in ${viewing.name} sees and edits the same ones.`
                : `What ${viewing.name} has turned on. You have read-only access, so you can open every setup but not change one.`
              : "Turn automations on for your organization."
          }
          tabs={<AutomationsSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          {running.length === 0 && available.length === 0 ? (
            <div className="callout">
              <p className="text-sm text-ink-muted">
                There are no automations to turn on yet. An admin defines them
                under Admin → Automations.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              <OverviewSection
                title="Running"
                groups={running}
                empty={`Nothing is running for ${orgName} yet.`}
                canEdit={can_edit}
              />
              <OverviewSection
                title="Available"
                groups={available}
                empty={`Everything is running for ${orgName}.`}
                canEdit={can_edit}
              />
            </div>
          )}
        </div>
      </AppShell>
    </>
  )
}

/**
 * Running or Available. The count is of setups, not automations: twenty
 * meeting reminders switched on is twenty things running.
 */
function OverviewSection({
  title,
  groups,
  empty,
  canEdit,
}: {
  title: string
  groups: AutomationGroup[]
  empty: string
  canEdit: boolean
}) {
  const count = groups.reduce((sum, group) => sum + group.setups.length, 0)

  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <h2>{title}</h2>
        <Badge tone="neutral">{count}</Badge>
      </div>

      {groups.length === 0 ? (
        <div className="callout">
          <p className="text-sm text-ink-muted">{empty}</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {groups.map((group) => (
            <AutomationGroupRow key={group.id} group={group} canEdit={canEdit} />
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * One automation, with this organization's setups of it.
 *
 * Almost every automation has exactly one, and renders exactly as it always
 * has. Only an automation an admin marked repeatable — a meeting reminder, one
 * per Local — ever has more, and then the automation's name is said once at the
 * top rather than repeated down twenty rows.
 *
 * The layout follows the automation's total, not what landed in this section:
 * a repeatable automation split between Running and Available keeps its header
 * in both, and says how many of its setups each one holds.
 */
function AutomationGroupRow({ group, canEdit }: { group: AutomationGroup; canEdit: boolean }) {
  if (group.total_setups === 1) {
    return (
      <li className="rounded-md border border-hairline bg-page">
        <SetupRow group={group} setup={group.setups[0]} leadWithAutomation canEdit={canEdit} />
      </li>
    )
  }

  return (
    <li className="rounded-md border border-hairline bg-page">
      <div className="border-b border-hairline px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ink-display">
            {group.name}
          </span>
          <Badge tone="neutral">
            {group.setups.length === group.total_setups
              ? `${group.total_setups} setups`
              : `${group.setups.length} of ${group.total_setups} setups`}
          </Badge>
        </div>
        {group.description && (
          <p className="mt-1 text-xs text-ink-muted">{group.description}</p>
        )}
      </div>

      <ul className="divide-y divide-hairline">
        {group.setups.map((setup) => (
          <li key={setup.setup_id}>
            <SetupRow group={group} setup={setup} canEdit={canEdit} />
          </li>
        ))}
      </ul>
    </li>
  )
}

// What happened the last time this setup fired. Links straight to the run so
// "why did only 10,147 of 10,576 get it?" is one click away.
function LastRunLine({ run }: { run: LastRun }) {
  const troubled = run.status === "refused" || run.status === "failed"

  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-muted">
      <Link href={`/runs/${run.id}`} className="no-underline">
        Last run {run.started_at_label ?? "—"}
      </Link>
      <span className={troubled ? "text-danger-display" : undefined}>
        {run.summary_label}
      </span>
      {run.was_dry_run && <Badge tone="signal">Rehearsal</Badge>}
    </p>
  )
}

/**
 * Where a setup sends, and — when that is a hand-picked set rather than the
 * whole organization — a way to read the list in full.
 *
 * A setup covering everything has nothing to open: "All 20 Locals" is already
 * the complete answer, and a dialog listing every Local would be a click that
 * tells you what the summary just said. Covering everything is also evaluated
 * live, so a fixed list would misrepresent it the moment a Local is added.
 */
function TargetSummary({ setup, title }: { setup: AutomationCard; title: string }) {
  const label = (
    <>
      <MapPin className="h-3.5 w-3.5" />
      {setup.target_summary}
    </>
  )

  if (setup.target_names.length === 0) {
    return <span className="inline-flex items-center gap-1.5">{label}</span>
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="toggle-button inline-flex items-center gap-1.5"
        >
          {label}
        </button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Where {title} sends</DialogTitle>
          <DialogDescription>
            {setup.target_summary} in {title}&rsquo;s setup. Only members of
            these receive it.
          </DialogDescription>
        </DialogHeader>
        <ul className="max-h-72 overflow-y-auto">
          {setup.target_names.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Removing a setup is not undoable, and the thing people will worry about is
 * the history — so the confirmation says outright that it is kept.
 */
function RemoveSetup({
  group,
  setup,
  title,
}: {
  group: AutomationGroup
  setup: AutomationCard
  title: string
}) {
  const remove = () => {
    router.delete(`/automations/${group.id}/setup/${setup.setup_id}`, {
      preserveScroll: true,
    })
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Trash2 className="h-3.5 w-3.5" />
          Remove
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Remove {title}?</DialogTitle>
          <DialogDescription>
            This setup stops running and its Locals, schedule and wording are
            deleted. Everything it already sent stays in History and is still
            named, so nothing is lost from the record.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Keep it</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button variant="danger" onClick={remove}>
              Remove setup
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SetupRow({
  group,
  setup,
  leadWithAutomation = false,
  canEdit,
}: {
  group: AutomationGroup
  setup: AutomationCard
  /** True when this is the only setup, so the row is titled by the automation. */
  leadWithAutomation?: boolean
  canEdit: boolean
}) {
  const setupHref = setup.setup_id
    ? `/automations/${group.id}/setup/${setup.setup_id}`
    : `/automations/${group.id}/setup`

  // Admin-only, and enqueued rather than run inline — a District Council's
  // audience runs to five figures, so the request returns long before the run
  // has finished writing its delivery rows.
  const runNow = () => {
    router.post(
      setup.setup_id
        ? `/automations/${group.id}/run/${setup.setup_id}`
        : `/automations/${group.id}/run`,
      {},
      { preserveScroll: true },
    )
  }

  // The next setup is made by copying this one, so only the Local, the time and
  // the wording need changing. It arrives switched off.
  const duplicate = () => {
    router.post(
      `/automations/${group.id}/setup/${setup.setup_id}/duplicate`,
      {},
      { preserveScroll: true },
    )
  }

  const title = leadWithAutomation ? group.name : (setup.label ?? "Setup")
  const canCopy = canEdit && group.allows_repeat_setups && setup.setup_id !== null
  const canRemove = canCopy && group.total_setups > 1

  return (
    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link href={setupHref} className="no-underline">
            <span className="text-sm font-medium text-ink-display">{title}</span>
          </Link>
          {leadWithAutomation && setup.label && (
            <span className="text-xs text-ink-muted">{setup.label}</span>
          )}
          <Badge tone={setup.is_enabled ? "accent" : "muted"}>
            {setup.is_enabled ? "On" : "Off"}
          </Badge>
          {setup.configured && setup.is_rehearsal && <Badge tone="signal">Rehearsal</Badge>}
          {setup.customized && <Badge tone="neutral">Custom wording</Badge>}
        </div>

        {leadWithAutomation && setup.description && (
          <p className="mt-1 text-xs text-ink-muted">{setup.description}</p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            {setup.schedule_label} at {setup.send_time_label}{" "}
            {setup.time_zone_abbreviation}
          </span>
          <TargetSummary setup={setup} title={title} />
          {setup.is_enabled && <span>Next run {setup.next_run_label}</span>}
        </div>

        {setup.last_run && <LastRunLine run={setup.last_run} />}

        {setup.last_changed_by && (
          <p className="mt-1 text-xs text-ink-muted">
            Last changed by {setup.last_changed_by}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
        {setup.can_run && (
          <Button variant="secondary" size="sm" onClick={runNow}>
            <Play className="h-3.5 w-3.5" />
            Run now
          </Button>
        )}
        {canCopy && (
          <Button variant="ghost" size="sm" onClick={duplicate}>
            <Copy className="h-3.5 w-3.5" />
            Duplicate
          </Button>
        )}
        {canRemove && <RemoveSetup group={group} setup={setup} title={title} />}
        <Link
          href={setupHref}
          className="inline-flex items-center gap-1 text-xs text-ink-muted no-underline hover:text-ink-display"
        >
          {canEdit ? "Set up" : "View"}
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  )
}
